<template>
  <div class="onboard-load" v-if="isOnboarding">
    <div class="onboard-load__left" :class="{ '-open': videoReady }"></div>

    <div class="onboard-load__right" :class="{ '-open': videoReady }"></div>

    <transition name="fade" mode="out-in">
      <div class="onboard-load__label" v-if="!videoReady">
        <Icon id="arrowDown" v-reveal="{ delay: 300 }"></Icon>
        <div v-reveal="{ delay: 100 }">{{ locale.relax }}</div>
      </div>
    </transition>
  </div>

  <nav class="navbar" :class="{ '-onboard': isOnboarding }">
    <img src="/img/Logo_EN.svg" class="navbar__logo" />
    <div class="navbar__links">
      <p>{{ locale.nav.referral }}</p>
      <a :href="`tel:18774228060`">1.877.422.8060</a>
      <a href="mailto:dveilleux@dvsf.ca">DVEILLEUX@DVSF.CA</a>
    </div>
  </nav>

  <div v-if="isOnboarding" class="onboard">
    <transition name="fade" appear :duration="1000">
      <div class="onboard__content" v-if="!reset && videoReady">
        <div class="onboard__hat">
          <p v-reveal="{ delay: 1000, class: 'v-reveal', activeClass: 'v-reveal-active' }">
            DAVID VEILLEUX FAMILY OFFICE
          </p>
          <Icon
            v-reveal="{ delay: 1100, class: 'v-reveal', activeClass: 'v-reveal-active' }"
            id="arrowDown"
            class="onboard__hat-icon"
          />
        </div>

        <h1 class="onboard__title" :key="`title-${currentThemeId}`">
          <AnimatedTitle
            :color="currentTheme.color"
            slow
            :delay="1200"
            :content="{ title: currentTheme.title()[0] }"
          />
          <AnimatedTitle slow :delay="2000" :content="{ title: currentTheme.title()[1] }" />
          <AnimatedTitle slow :delay="2800" :content="{ title: currentTheme.title()[2] }" />
        </h1>

        <div v-reveal="{ delay: 3800, duration: 500 }">
          <button
            @click="selectedThemeId = currentThemeId"
            class="onboard__button"
            :key="`button-${currentTheme.cta[$i18n.locale]}`"
          >
            {{ currentTheme.cta[$i18n.locale] }}
          </button>
        </div>
      </div>
      <div v-else></div>
    </transition>
  </div>

  <div v-else class="content">
    <div>
      <div class="site-title" :class="{ '-fr': $i18n.locale === 'fr' }">
        <p :style="`color: ${currentTheme.color}`">
          <AnimatedTitle :content="{ title: $page?.title[0] || locale.title.line1 }" :delay="300" />
        </p>
        <div class="site-subtitle__row">
          <div class="site-subtitle" :class="{ '-fr': $i18n.locale === 'fr' }" v-if="mq.mdPlus">
            <div v-reveal="{ delay: 200 }">
              <icon :style="`color: ${currentTheme.color}; font-size: 1.25rem;`" id="arrowDown" />
            </div>
            <div v-reveal="{ delay: 300 }">
              <div>{{ $page?.subtitle[0] || locale.subtitle.line1 }}</div>
            </div>
            <div v-reveal="{ delay: 330 }">
              <div>{{ $page?.subtitle[1] || locale.subtitle.line2 }}</div>
            </div>
            <div v-reveal="{ delay: 360 }">
              <div>{{ $page?.subtitle[2] || locale.subtitle.line3 }}</div>
            </div>
          </div>

          <div :style="`color: ${currentTheme.color}`">
            <AnimatedTitle
              :content="{ title: $page?.title[1] || locale.title.line2 }"
              :delay="800"
            />
          </div>

          <div
            v-if="mq.mdPlus"
            style="
              flex: 1;
              display: inline-flex;
              align-items: center;
              justify-content: flex-end;
              align-self: flex-end;
              margin-bottom: 0.5rem;
            "
            @click="scroll"
            v-reveal="{ delay: 800 }"
          >
            <DoubleButton :label="$page?.guide || locale.guide" :theme="currentTheme" />
          </div>
        </div>
        <div class="site-title-end">
          <AnimatedTitle
            :content="{ title: $page?.title[2] || locale.title.line3 }"
            :delay="1200"
          />
        </div>
      </div>

      <div class="site-title__bottom">
        <button class="button-swipe" @click="scroll">
          <div class="button-swipe__label">
            <div class="site-title__bottom-row">
              <Icon id="arrowDownAlt" />
              <div>{{ $page?.scroll || locale.scroll }}</div>
            </div>
          </div>

          <div class="button-swipe__label-2">
            <div class="site-title__bottom-row">
              <Icon id="arrowDownAlt" />
              <div>{{ $page?.scroll || locale.scroll }}</div>
            </div>
          </div>
        </button>

        <button @click="switchLang()" v-reveal="{ delay: 650 }" class="button-swipe -lang">
          <div class="button-swipe__label">
            {{ $i18n.locale === 'fr' ? 'ENGLISH' : 'FRANÇAIS' }}
          </div>
          <div class="button-swipe__label-2">
            {{ $i18n.locale === 'fr' ? 'ENGLISH' : 'FRANÇAIS' }}
          </div>
        </button>
      </div>

      <div class="site-subtitle site-subtitle--mobile">
        <p>
          <icon :style="`color: ${currentTheme.color}; font-size: 1.25rem;`" id="arrowDown" />
        </p>
        <p>{{ $page?.subtitle[0] || locale.subtitle.line1 }}</p>
        <p>{{ $page?.subtitle[1] || locale.subtitle.line2 }}</p>
        <p>{{ $page?.subtitle[2] || locale.subtitle.line3 }}</p>
      </div>
      <div
        v-if="mq.mdMinus"
        @click="scroll"
        style="
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: flex-end;
          align-self: flex-end;
          margin-top: 1rem;
        "
      >
        <DoubleButton :label="$page?.guide || locale.guide" :theme="currentTheme" />
      </div>
    </div>

    <section class="section-1" ref="section1">
      <div
        class="section-1__left"
        v-reveal="{ delay: 300 }"
        v-html="$page?.section1.title || locale.section1.title"
        v-parallax
      />

      <div class="section-1__right" v-reveal="{ delay: 450 }" v-parallax>
        <div>{{ $page?.section1.description || locale.section1.description }}</div>
      </div>
    </section>

    <section
      class="section-circle"
      ref="circle"
      :style="`border-color: ${currentTheme.color}`"
      v-parallax="{ speed: 0.07 }"
    >
      <div
        class="section-circle__text"
        v-reveal="{
          class: 'v-reveal-slide-right',
          activeClass: 'v-reveal-slide-right-active',
          delay: 200,
        }"
      >
        {{ $page?.section1.items[0] || locale.section1.items[0] }}
        <div
          class="section-circle__arrow"
          ref="arrow"
          :style="`color: ${currentTheme.color}; left: ${arrowLeft}px`"
        >
          <Icon id="arrowDown"></Icon>
        </div>
      </div>

      <div
        class="section-circle__text"
        v-reveal="{
          class: 'v-reveal-slide-right',
          activeClass: 'v-reveal-slide-right-active',
          delay: 350,
        }"
      >
        {{ $page?.section1.items[1] || locale.section1.items[1] }}
        <div
          class="section-circle__arrow"
          ref="arrow"
          :style="`color: ${currentTheme.color}; left: ${arrowLeft}px`"
        >
          <Icon id="arrowDown"></Icon>
        </div>
      </div>

      <div
        class="section-circle__text"
        v-reveal="{
          class: 'v-reveal-slide-right',
          activeClass: 'v-reveal-slide-right-active',
          delay: 500,
        }"
      >
        {{ $page?.section1.items[2] || locale.section1.items[2] }}
        <div
          class="section-circle__arrow"
          ref="arrow"
          :style="`color: ${currentTheme.color}; left: ${arrowLeft}px`"
        >
          <Icon id="arrowDown"></Icon>
        </div>
      </div>

      <div
        class="section-circle__text"
        v-reveal="{
          class: 'v-reveal-slide-right',
          activeClass: 'v-reveal-slide-right-active',
          delay: 650,
        }"
      >
        {{ $page?.section1.items[3] || locale.section1.items[3] }}
        <div
          class="section-circle__arrow"
          ref="arrow"
          :style="`color: ${currentTheme.color}; left: ${arrowLeft}px`"
        >
          <Icon id="arrowDown"></Icon>
        </div>
      </div>
    </section>

    <section class="section-relation">
      <div class="section-relation__top">
        <div class="section-relation__top--title1" v-reveal="{ delay: 200 }" v-parallax>
          <div>{{ $page?.section2.title[0] || locale.section2.title.line1 }}</div>
        </div>
        <div class="section-relation__top--title2" v-reveal="{ delay: 250 }" v-parallax>
          <div>{{ $page?.section2.title[1] || locale.section2.title.line2 }}</div>
        </div>
      </div>

      <h2 class="section-relation__title" v-reveal="{ delay: 300 }" v-parallax>
        <div v-html="$page?.section2.title[2] || locale.section2.title.line3"></div>

        <div
          v-bg-parallax
          :style="{ backgroundImage: `url(${themes[selectedThemeId].images[0]})` }"
          class="section-relation__image --mb"
          v-reveal="{ delay: 350, class: 'v-reveal', activeClass: 'v-reveal-active' }"
        />
      </h2>
      <div
        class="section-relation__content"
        v-reveal="{ delay: 450 }"
        v-parallax="{ speed: 0.035 }"
      >
        <p>
          {{ $page.section2.text || locale.section2.text }}
        </p>
      </div>

      <div
        v-bg-parallax
        :style="{ backgroundImage: `url(${themes[selectedThemeId].images[0]})` }"
        class="section-relation__image --dt"
        v-reveal="{ delay: 350, class: 'v-reveal', activeClass: 'v-reveal-active' }"
      />
    </section>

    <section class="section-services">
      <div
        class="section-services__service-wrap"
        v-for="(service, index) in services || locale.services"
        :id="`service-${service.id}`"
        :key="service.image"
        @mousemove="handleMouseMove"
        @mouseenter="activeService = service"
        @mouseleave="activeService = null"
      >
        <div class="section-services__service">
          <div class="section-services__service--title" v-reveal="{ delay: index * 50 }">
            <h2>{{ service.title }}</h2>
          </div>
          <FloatingSvg
            :id="service.id"
            class="section-services__service--image"
            v-reveal="{
              class: 'v-reveal-slide-right',
              activeClass: 'v-reveal-slide-right-active',
              delay: index * 50,
            }"
          />
        </div>
        <div class="section-services__service--text" v-reveal="{ delay: index * 50 }">
          <p>{{ service.text }}</p>
        </div>
      </div>
    </section>

    <section class="section-connect">
      <div>
        <div class="section-connect__title" v-reveal="{ delay: 150 }" v-parallax>
          <h2 v-html="$page?.section3.title || locale.section3.title"></h2>
        </div>
        <div class="section-connect__text" v-reveal="{ delay: 350 }" v-parallax="{ speed: 0.035 }">
          <p>
            {{ $page?.section3.description || locale.section3.description }}
          </p>
        </div>
        <DoubleButton
          url="https://dvsf.ca/cabinet"
          :label="$page?.section3.button || locale.section3.button"
          :theme="currentTheme"
          v-reveal="{ delay: 650 }"
          v-parallax="{ speed: 0.03 }"
        />
      </div>
      <div
        v-bg-parallax="{ ratio: 18 }"
        :style="{ backgroundImage: `url(${themes[selectedThemeId].images[1]})` }"
        class="section-connect__image"
        v-reveal="{ delay: 350, class: 'v-reveal', activeClass: 'v-reveal-active' }"
      />
    </section>

    <section class="section-journey">
      <div class="section-journey__title" v-parallax="{ speed: 0.03 }">
        <div v-reveal="{ delay: 150 }">
          <h2>{{ $page?.section4.title[0] || locale.section4.title.line1 }}</h2>
        </div>
        <div v-reveal="{ delay: 300 }">
          <h2>{{ $page?.section4.title[1] || locale.section4.title.line2 }}</h2>
        </div>
      </div>

      <div class="section-journey__content">
        <div v-reveal="{ delay: 450 }" v-parallax="{ speed: 0.01 }">
          <h2
            class="section-journey__content--title"
            v-html="$page?.section4.title[3] || locale.section4.title.line3"
          ></h2>
        </div>

        <div class="section-journey__content--row">
          <div>
            <div v-reveal="{ delay: 500 }">
              <p class="section-journey__content--text">
                {{ $page?.section4.text || locale.section4.text }}
              </p>
            </div>
            <DoubleButton
              :action="() => openLink('mailto:dveilleux@dvsf.ca')"
              :label="$page?.section4.button || locale.section4.button"
              :theme="currentTheme"
              v-reveal="{ delay: 650 }"
            />
          </div>

          <div v-reveal="{ delay: 530 }">
            <a
              href="https://maps.app.goo.gl/M6mkCUndPgLs2NWR8"
              target="_blank"
              class="section-journey__content--contact"
            >
              <Icon id="arrowDown" :style="`color: ${currentTheme.color};`" />
              <p>5700 BOUL. DES GALERIES,</p>
              <p>SUITE 405</p>
              <p>QUÉBEC (QC) G2K 0H5</p>
            </a>
          </div>
        </div>
      </div>
      <div
        v-bg-parallax="{ direction: 1, ratio: 18, speed: 0.35 }"
        :style="{ backgroundImage: `url(${themes[selectedThemeId].images[2]})` }"
        class="section-journey__image"
        v-reveal="{ delay: 350, class: 'v-reveal', activeClass: 'v-reveal-active' }"
      />
    </section>

    <footer class="section-footer" v-html="locale.footnote"></footer>

    <IntersiteNav v-if="$beet.isReady" :theme="currentTheme" />
  </div>

  <div
    class="onboard__video"
    :class="{ '-side': !isOnboarding }"
    :style="
      isOnboarding
        ? ''
        : `top: calc(${mq.smMinus ? '17rem' : '6.25rem'} + ${scrollPosition > 10 && scrollPosition < 500 ? 0.2 * scrollPosition : 0}px)`
    "
  >
    <video
      autoplay
      loop
      playsinline
      muted
      class="onboard__video-video"
      v-for="(theme, index) in themes"
      preload="auto"
      :ref="`videoPlayers-${index}`"
      :class="{ '-active': currentThemeId === index }"
      :key="`video-${index}`"
    >
      <source :src="theme.video" type="video/mp4" />
    </video>
  </div>

  <div class="lang-mobile" :class="{ '-white': isOnboarding }">
    <button @click="switchLang()">
      <div class="button-swipe__label">{{ $i18n.locale === 'fr' ? 'ENGLISH' : 'FRANÇAIS' }}</div>
    </button>
  </div>
</template>

<script>
import IntersiteNav from '@/components/navigation/IntersiteNav.vue'
import { setMeta } from '@/helpers/metaHelpers'

import AnimatedTitle from '../components/AnimatedTitle.vue'
import DoubleButton from '../components/small/DoubleButton.vue'
import FloatingSvg from '../components/small/FloatingSvg.vue'

export default {
  name: 'Home',
  components: { DoubleButton, FloatingSvg, AnimatedTitle, IntersiteNav },
  beetPage: 'home',
  inject: ['mq'],
  data() {
    return {
      isOnboarding: true,
      videoInterval: null,
      currentThemeId: 0,
      selectedThemeId: null,
      activeService: null,
      followerX: 0,
      followerY: 0,
      mouseX: 0,
      mouseY: 0,
      readyVideos: [],
      scrollPosition: 0,
      lastScrollPosition: 0,
      scrollProgress: 0,
      arrowLeft: 0,
      videoReady: false,
      onboardTimer: false,
      reset: false,
    }
  },
  beforeMount() {
    this.currentThemeId = Math.floor(Math.random() * this.themes.length)
    setMeta({
      title: 'DAVID VEILLEUX FAMILY OFFICE',
      description: "PROTÉGER LES FAMILLES, CULTIVER L'HÉRITAGE".toLowerCase(),
    })
  },
  unmounted() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  mounted() {
    setTimeout(() => {
      this.onboardTimer = true
    }, 1000)
    const ref = this.$refs[`videoPlayers-${this.currentThemeId}`][0]
    ref.addEventListener('canplaythrough', () => {
      if (!this.onboardTimer) {
        setTimeout(() => {
          this.videoReady = true
        }, 1000)
      } else {
        this.videoReady = true
      }
    })

    window.addEventListener('scroll', this.handleScroll)

    this.$nextTick(() => {
      this.videoInterval = setInterval(() => {
        this.reset = true
        if (this.currentThemeId === this.themes.length - 1) {
          this.$refs[`videoPlayers-${this.currentThemeId}`][0].pause()
          this.currentThemeId = 0
          this.$refs['videoPlayers-0'][0].playbackRate = 0.6
          this.$refs['videoPlayers-0'][0].play()
        } else {
          this.$refs[`videoPlayers-${this.currentThemeId}`][0].pause()
          this.currentThemeId += 1
          this.$refs[`videoPlayers-${this.currentThemeId}`][0].playbackRate = 0.6
          this.$refs[`videoPlayers-${this.currentThemeId}`][0].play()
        }

        setTimeout(() => {
          this.reset = false
        }, 100)
      }, 8000)
    })

    const animateFollower = () => {
      this.followerX += this.mouseX - this.followerX
      this.followerY += this.mouseY - this.followerY
      requestAnimationFrame(animateFollower)
    }

    requestAnimationFrame(animateFollower)
  },
  watch: {
    $page(to) {
      if (to) {
        setMeta(to.seo)
      }
    },
    selectedThemeId(to) {
      if (to !== undefined) {
        this.$nextTick(() => {
          clearInterval(this.videoInterval)
          this.videoInterval = null
          document.documentElement.style.setProperty('--current-color', this.themes[to].color)
          this.isOnboarding = false
        })
      }
    },
  },
  computed: {
    services() {
      if (!this.$page) return null
      const arr = this.$page.services.map((item, index) => {
        return {
          ...item,
          id: index + 1,
          image: this.locale.services[index].image,
        }
      })
      return arr
    },
    themes() {
      return [
        {
          color: '#B7D5E8',
          video: '/video/charting.mp4',
          title: () => {
            if (this.$i18n.locale === 'fr') {
              return ['BALISER LE CHEMIN', 'GUIDER VOTRE RICHESSE', 'VERS DES HAVRES SÛRS']
            } else {
              return ['CHARTING THE COURSE', 'STEERING YOUR WEALTH', 'TO SAFE HARBORS']
            }
          },
          cta: {
            fr: 'COMMENCEZ VOTRE VOYAGE',
            en: 'START YOUR JOURNEY',
          },
          images: ['/img/boats.jpg', '/img/boats_2.jpg', '/img/boats_3.jpg'],
        },
        {
          color: '#C1C99F',
          video: '/video/aged.mp4',
          title: () => {
            if (this.$i18n.locale === 'fr') {
              return ['PARFAIT AVEC LE TEMPS', 'CULTIVER LA PROSPÉRITÉ', 'AVEC PRÉCISION']
            } else {
              return ['AGED TO PERFECTION', 'CURATING PROSPERITY', 'WITH PRECISION']
            }
          },
          cta: {
            fr: 'DÉCOUVREZ VOTRE CHEMIN',
            en: 'UNCOVER YOUR PATH',
          },
          images: ['/img/nature.jpg', '/img/nature_2.jpg', '/img/nature_3.jpg'],
        },
        {
          color: '#A4C6B8 ',
          video: '/video/catch.mp4',
          title: () => {
            if (this.$i18n.locale === 'fr') {
              return ["L'ART DE LA PRISE", 'STRATÉGIES SUR MESURE', 'POUR INVESTISSEURS ÉLITES']
            } else {
              return ['THE ART OF THE CATCH', 'TAILORED STRATEGIES', 'FOR ELITE INVESTORS']
            }
          },
          cta: {
            fr: 'LANCEZ VOTRE LIGNE',
            en: 'CAST YOUR LINE',
          },
          images: ['/img/wild.jpg', '/img/wild_2.jpg', '/img/wild_3.jpg'],
        },
      ]
    },
    locale() {
      const data = this.$store.state.content[this.$i18n.locale]
      return data
    },
    currentTheme(to) {
      return this.themes[this.currentThemeId]
    },
  },
  methods: {
    openLink(url) {
      window.open(url, '_blank')
    },
    scroll() {
      let pageHeight = window.innerHeight
      if (window.innerHeight > 900) {
        pageHeight = pageHeight * 0.8
      }
      window.scrollTo({ left: 0, top: pageHeight, behavior: 'smooth' })
    },
    handleMouseMove(e) {
      this.mouseX = e.pageX / 8
      this.mouseY = e.pageY + 240
    },
    handleScroll() {
      this.scrollPosition = window.scrollY

      if (this.activeService) {
        this.mouseY += this.scrollPosition - this.lastScrollPosition
      }

      const circle = this.$refs.circle
      const offset = window.innerHeight / 2
      if (
        this.mq.smPlus &&
        this.scrollPosition > offset &&
        this.scrollPosition < circle.offsetTop + window.innerHeight
      ) {
        const newval = (this.scrollPosition - window.innerHeight) / 2
        if (newval > -50 && newval < 230) {
          this.arrowLeft = newval
        }
      }
      if (this.mq.smMinus) {
        this.arrowLeft = 38
      }

      this.lastScrollPosition = this.scrollPosition
    },
    switchLang() {
      if (this.$i18n.locale === 'fr') this.$i18n.locale = 'en'
      else this.$i18n.locale = 'fr'
    },
  },
}
</script>
